import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, resolveFormattingLocale } from '@eis/core';
import type { BillingApi } from '@eis/billingservice-sdk';
import {
  ExportButton,
  ConfirmAction,
  DetailsSection,
  EmptyState,
  EntityHeader,
  ErnLine,
  ErrorState,
  PageLayout,
  RequirePermission,
  StatusChip,
} from '@eis/patterns';
import { billingPermissions as p } from '../../../permissions';
import { buildInvoiceExportLabels } from '../model/export-labels';
import { isBillingUnavailable, isNotFound, useInvoice, useRevertInvoice } from '../api/invoices';
import {
  civilOf,
  codeOfErn,
  formatMinor,
  invoiceTitle,
  isDeltaType,
  isRevertible,
  periodLabel,
} from '../model/types';
import { RefusalAlert } from './BillingDialogs';

/**
 * One invoice (SDD-015 §7.1): the legacy document, read whole.
 *
 * The header answers "which document, for whom, in what state"; the sections answer where the
 * money came from — the items (with previous → new on a document that re-states money), the
 * references to the priced statements behind them (B-3) and to the policy, the recipient (a
 * partner's pinned version, B-12) and the billing rules it was priced under. Nothing here is
 * computed: an invoice is insert-only (B-4) and the screen renders what it carries.
 *
 * The one action is REVERT (`invoices.correct`): a correction that negates every line while the
 * original moves to `refunded` (§6.3). Offered only where the service would accept it, and
 * confirmed with the amount the correction will carry.
 */
export function InvoiceDetailPage({ code }: { readonly code: string }): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const query = useInvoice(code, true);
  const revert = useRevertInvoice();
  const [confirming, setConfirming] = useState(false);
  const [reversal, setReversal] = useState<BillingApi.Invoice | null>(null);

  if (query.isPending) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }
  if (query.isError) {
    const unknown = isNotFound(query.error);
    const down = isBillingUnavailable(query.error);
    return (
      <PageLayout>
        <ErrorState
          description={
            unknown
              ? t('invoices.error.notFound', { code })
              : down
                ? t('invoices.error.unavailable')
                : t('invoices.error.detail')
          }
          details={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </PageLayout>
    );
  }

  const invoice = query.data.invoice;
  if (invoice === undefined) {
    return (
      <PageLayout>
        <EmptyState description={t('invoices.error.notFound', { code })} />
      </PageLayout>
    );
  }

  const title = invoiceTitle(invoice);
  const type = invoice.type ?? '';
  const status = invoice.status ?? '';
  const currency = invoice.currency ?? 'EUR';
  const money = (minor: number | undefined): string => formatMinor(minor, currency, locale);
  const delta = isDeltaType(type);
  const estimated = type === 'estimated';
  const revertible = isRevertible(invoice);
  const policyCode = invoice.policyCode ?? '';
  const groupPolicyCode = invoice.groupPolicyCode ?? '';
  const isMaster = groupPolicyCode !== '' && groupPolicyCode === policyCode;
  const items = invoice.invoiceItems ?? [];
  const statuses = invoice.statuses ?? [];
  const history = invoice.estimateHistory ?? [];
  const metadata = Object.entries(invoice.metadata ?? {});

  const policyLink = (target: string, master: boolean): ReactNode => (
    <Link
      component="button"
      type="button"
      sx={{ fontFamily: 'monospace' }}
      onClick={() => {
        void navigate({
          href: master
            ? links.insurance.groupPolicy(target, 'invoices')
            : links.insurance.policy(target),
        });
      }}
    >
      {target}
    </Link>
  );

  return (
    <PageLayout
      header={
        <EntityHeader
          title={title}
          subtitle={
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Chip
                size="small"
                label={t(`invoices.type.${type}` as never, { defaultValue: type })}
                data-testid="invoice-type"
              />
              <Typography variant="body2" color="text.secondary">
                {policyCode}
              </Typography>
            </Stack>
          }
          status={{
            value: status,
            label: t(`invoices.status.${status}` as never, { defaultValue: status }),
          }}
          // Berry has no breadcrumb band: the way back is the header's own link (prompt 24).
          backTo={{
            label: t('invoices.title'),
            href: '/billing/invoices',
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: '/billing/invoices' });
            },
          }}
          meta={[
            {
              label: t('detail.ern'),
              value: <ErnLine ern={invoice.ern} copyLabel={t('detail.ernCopy')} />,
            },
            {
              label: t('detail.code'),
              value: <span style={{ fontFamily: 'monospace' }}>{invoice.code}</span>,
            },
            {
              label: t('detail.period.createdAt'),
              value:
                civilOf(invoice.createdAt) === ''
                  ? '—'
                  : formatCivilDate(civilOf(invoice.createdAt), locale),
            },
          ]}
          actions={
            <Stack direction="row" spacing={1}>
              {/* Header and lines in one workbook (SDD-020 §8.3); finance reconciles from it. */}
              <ExportButton
                service="billing"
                permission={p.exports.create}
                buildDetailRequest={() => ({
                  resource: invoice.ern ?? `billingservice:invoice/${code}`,
                  labels: buildInvoiceExportLabels(t),
                  label: t('detail.exportLabel', { number: invoiceTitle(invoice) }),
                })}
              />
              <RequirePermission anyOf={[p.invoices.correct]}>
                <Tooltip title={revertible ? '' : t('revert.notRevertible')}>
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={!revertible || revert.isPending}
                      onClick={() => {
                        setConfirming(true);
                      }}
                    >
                      {t('invoices.actions.revert')}
                    </Button>
                  </span>
                </Tooltip>
              </RequirePermission>
            </Stack>
          }
        />
      }
    >
      <Stack spacing={2.5}>
        {estimated && <Alert severity="info">{t('detail.estimateNote')}</Alert>}
        {status === 'refunded' && reversal !== null && (
          <Alert severity="success" data-testid="reverted">
            {t('revert.done', { number: invoiceTitle(reversal) })}
          </Alert>
        )}
        {(invoice.revertsInvoiceErn ?? '') !== '' && (
          <Alert severity="info">
            {t('detail.reversalNote', { code: codeOfErn(invoice.revertsInvoiceErn) })}
          </Alert>
        )}
        <RefusalAlert error={revert.error} />

        <DetailsSection
          title={t('detail.amounts.title')}
          fields={[
            { label: t('detail.amounts.net'), value: money(invoice.netAmount) },
            { label: t('detail.amounts.tax'), value: money(invoice.taxAmount) },
            {
              label: t('detail.amounts.gross'),
              value: <strong>{money(invoice.grossAmount)}</strong>,
            },
            { label: t('detail.amounts.open'), value: money(invoice.openAmount) },
            { label: t('detail.amounts.paid'), value: money(invoice.paidAmount) },
            { label: t('detail.amounts.credit'), value: money(invoice.creditAmount) },
          ]}
          advancedFields={[
            { label: t('detail.amounts.total'), value: money(invoice.totalAmount) },
            { label: t('detail.amounts.dunningFees'), value: money(invoice.dunningFees) },
            { label: t('detail.amounts.currency'), value: currency },
          ]}
        />

        <DetailsSection
          title={t('detail.period.title')}
          fields={[
            {
              label: t('detail.period.period'),
              value: periodLabel(
                civilOf(invoice.billingIntervalFrom),
                civilOf(invoice.billingIntervalTo),
                locale,
              ),
            },
            {
              label: t('detail.period.due'),
              value:
                civilOf(invoice.dueDate) === ''
                  ? '—'
                  : formatCivilDate(civilOf(invoice.dueDate), locale),
            },
            {
              label: t('detail.period.createdBy'),
              value: invoice.createdBy === '' ? '—' : invoice.createdBy,
            },
            ...(estimated
              ? [
                  {
                    label: t('detail.period.estimatedAt'),
                    value:
                      civilOf(invoice.estimatedAt) === ''
                        ? '—'
                        : formatCivilDate(civilOf(invoice.estimatedAt), locale),
                  },
                ]
              : []),
          ]}
        />

        <DetailsSection
          title={t('detail.references.title')}
          description={t('detail.references.description')}
          fields={[
            {
              label: isMaster ? t('detail.references.groupPolicy') : t('detail.references.policy'),
              value: policyCode === '' ? '—' : policyLink(policyCode, isMaster),
            },
            ...(!isMaster && groupPolicyCode !== ''
              ? [
                  {
                    label: t('detail.references.groupPolicy'),
                    value: policyLink(groupPolicyCode, true),
                  },
                ]
              : []),
            {
              label: t('detail.references.recipient'),
              value:
                (invoice.recipientPartnerId ?? '') === '' ? (
                  t('detail.references.none')
                ) : (
                  <Link
                    component="button"
                    type="button"
                    sx={{ fontFamily: 'monospace' }}
                    onClick={() => {
                      void navigate({
                        href: links.partners.partner(invoice.recipientPartnerId ?? ''),
                      });
                    }}
                  >
                    {invoice.recipientPartnerId}
                  </Link>
                ),
            },
            {
              label: t('detail.references.recipientVersion'),
              value: String(invoice.recipientPartnerVersion ?? '—'),
            },
          ]}
          advancedFields={[
            { label: t('detail.references.bill'), value: mono(invoice.billErn) },
            { label: t('detail.references.previousBill'), value: mono(invoice.previousBillErn) },
            { label: t('detail.references.reverts'), value: mono(invoice.revertsInvoiceErn) },
            { label: t('detail.references.billingConfig'), value: mono(invoice.billingConfigErn) },
            { label: t('detail.references.paymentMethod'), value: mono(invoice.paymentMethodCode) },
            ...metadata.map(([key, value]) => ({
              label: key,
              value: mono(typeof value === 'string' ? value : JSON.stringify(value)),
            })),
          ]}
        />

        <Stack spacing={1} component="section" aria-labelledby="invoice-items">
          <Typography variant="h3" component="h2" id="invoice-items">
            {t('detail.items.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('detail.items.description')}
          </Typography>
          {items.length === 0 ? (
            <EmptyState description={t('detail.items.empty')} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-labelledby="invoice-items">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('detail.items.name')}</TableCell>
                    <TableCell>{t('detail.items.policy')}</TableCell>
                    <TableCell>{t('detail.items.period')}</TableCell>
                    <TableCell align="right">{t('detail.items.quantity')}</TableCell>
                    <TableCell align="right">{t('detail.items.unitPrice')}</TableCell>
                    {delta && <TableCell align="right">{t('detail.items.previous')}</TableCell>}
                    {delta && <TableCell align="right">{t('detail.items.new')}</TableCell>}
                    <TableCell align="right">{t('detail.items.net')}</TableCell>
                    <TableCell align="right">{t('detail.items.tax')}</TableCell>
                    <TableCell align="right">{t('detail.items.gross')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow
                      key={`${item.policyCode ?? ''}-${String(index)}`}
                      data-testid="invoice-item"
                    >
                      <TableCell>{item.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {item.policyCode ?? policyCode}
                      </TableCell>
                      <TableCell>
                        {periodLabel(
                          civilOf(item.billingIntervalFrom),
                          civilOf(item.billingIntervalTo),
                          locale,
                        )}
                      </TableCell>
                      <TableCell align="right">{item.quantity ?? '—'}</TableCell>
                      <TableCell align="right">{item.pricePerUnit ?? '—'}</TableCell>
                      {delta && <TableCell align="right">{money(item.previousAmount)}</TableCell>}
                      {delta && <TableCell align="right">{money(item.newAmount)}</TableCell>}
                      <TableCell align="right">{money(item.netAmount)}</TableCell>
                      <TableCell align="right">{money(item.taxAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {money(item.grossAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>

        {estimated && history.length > 0 && (
          <Stack spacing={1} component="section" aria-labelledby="estimate-history">
            <Typography variant="h3" component="h2" id="estimate-history">
              {t('detail.history.title')}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-labelledby="estimate-history">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('detail.history.estimatedAt')}</TableCell>
                    <TableCell align="right">{t('detail.history.gross')}</TableCell>
                    <TableCell>{t('detail.history.bill')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((entry) => (
                    <TableRow key={entry.billErn}>
                      <TableCell>
                        {civilOf(entry.estimatedAt) === ''
                          ? '—'
                          : formatCivilDate(civilOf(entry.estimatedAt), locale)}
                      </TableCell>
                      <TableCell align="right">{money(entry.grossAmount)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {codeOfErn(entry.billErn)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        <Stack spacing={1} component="section" aria-labelledby="invoice-statuses">
          <Typography variant="h3" component="h2" id="invoice-statuses">
            {t('detail.statuses.title')}
          </Typography>
          {statuses.length === 0 ? (
            <EmptyState description={t('detail.statuses.empty')} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-labelledby="invoice-statuses">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('detail.statuses.status')}</TableCell>
                    <TableCell>{t('detail.statuses.at')}</TableCell>
                    <TableCell>{t('detail.statuses.by')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.map((entry, index) => (
                    <TableRow key={`${entry.status ?? ''}-${String(index)}`}>
                      <TableCell>
                        <StatusChip
                          status={entry.status ?? ''}
                          label={t(`invoices.status.${entry.status ?? ''}` as never, {
                            defaultValue: entry.status ?? '',
                          })}
                        />
                      </TableCell>
                      <TableCell>{entry.createdAt ?? '—'}</TableCell>
                      <TableCell>{entry.createdBy ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Stack>

      <ConfirmAction
        open={confirming}
        title={t('revert.title')}
        description={t('revert.description')}
        severity="destructive"
        confirmLabel={t('revert.confirm')}
        cancelLabel={t('revert.cancel')}
        busy={revert.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          void revert
            .mutateAsync({ invoiceCode: invoice.code ?? code, policyCode })
            .then((response) => {
              setReversal(response.invoice ?? null);
              setConfirming(false);
            })
            .catch(() => {
              setConfirming(false);
            });
        }}
      >
        <Typography variant="body2" data-testid="revert-preview">
          {t('revert.preview', { amount: money(-(invoice.grossAmount ?? 0)), number: title })}
        </Typography>
      </ConfirmAction>
    </PageLayout>
  );
}

const mono = (value: string | undefined): ReactNode =>
  value === undefined || value === '' ? (
    '—'
  ) : (
    <span style={{ fontFamily: 'monospace' }}>{value}</span>
  );
